import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import { getLocation, push, replace } from 'connected-react-router';
import { connect } from 'react-redux';
import Svg from 'svg-inline-react';
import { Helmet } from 'react-helmet';
import updeep from 'updeep';
import { matchPath } from 'react-router-dom';
import apolloClient from '@src/graphql/apollo';
import FunnelReviewView from '@src/views/CreateFunnel/FunnelReview/View';
import { setModalContent } from '@src/redux/actions/modal';
import { FUNNEL_PREVIEW_ROUTE, FUNNEL_ROUTE, OPENINGS_ROUTE } from '@src/Routes/links';
import { createLink } from '@src/util/createLink';
import { idify } from '@src/util/idify';
import { captureError } from '@src/util/error';
import { setErrorFlashMessage } from '@src/redux/actions/flashMessages';
import { getRecruiter, getCustomerStatus } from '@src/redux/selectors/recruiter';
import api from '@src/util/api';
import { checkSubscriptionState } from '@src/redux/actions/subscription';
import { checkOpeningLimit } from '@src/redux/actions/opening';
import {
  OpeningQueryDocument,
  OpeningStateEnum,
  RelocationBenefitsDocument,
  SaveOpeningDocument,
  PublishOpeningDocument,
} from '@src/graphql/generated';
import { FinishProfileModal } from '@src/Routes/FinishProfileModal';
import { getActiveMetricsContext, trackEvent } from '@src/metrics';
import { MetricAction, MetricContext, MetricEventSource } from '@src/metrics/metrics.enum';
import EditIconSvg from './edit-icon.svg';
import './styles.scss';

const PROFILE_FINISH_FLOWS = {
  EDIT: 'edit',
  PUBLISH: 'publish',
};

@connect(
  state => ({
    customerStatus: getCustomerStatus(state),
    location: getLocation(state),
    recruiter: getRecruiter(state),
  }),
  {
    checkOpeningLimit,
    checkSubscriptionState,
    push,
    replace,
    setErrorFlashMessage,
    setModalContent,
  },
)
export default class FunnelPreview extends PureComponent {
  static propTypes = {
    checkOpeningLimit: PropTypes.func.isRequired,
    checkSubscriptionState: PropTypes.func.isRequired,
    customerStatus: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
    push: PropTypes.func.isRequired,
    recruiter: PropTypes.object.isRequired,
    replace: PropTypes.func.isRequired,
    setErrorFlashMessage: PropTypes.func.isRequired,
    setModalContent: PropTypes.func.isRequired,
  };

  state = {
    companyProfile: undefined,
    opening: undefined,
    publishing: false,
    relocationBenefits: undefined,
  };

  async componentDidMount() {
    const { location } = this.props;

    const match = matchPath(location.pathname, {
      exact: false,
      path: FUNNEL_PREVIEW_ROUTE,
      strict: false,
    });

    const openingId = match?.params?.openingId;

    try {
      const [
        { companyProfile },
        {
          data: { relocationBenefits },
        },
        {
          data: { opening },
        },
      ] = await Promise.all([
        api.get('/api/v3/companies/company-profile'),
        apolloClient.query({
          query: RelocationBenefitsDocument,
        }),
        apolloClient.query({
          query: OpeningQueryDocument,
          variables: { openingId },
        }),
      ]);

      if (opening.state === OpeningStateEnum.ACTIVE) {
        window.location = `/employer/funnel/${opening.id}`;
      }

      this.setState({
        companyProfile,
        opening,
        relocationBenefits,
      });
    } catch (error) {
      this.props.setErrorFlashMessage('Opening not found.');
      captureError(error);
      setTimeout(() => {
        this.props.replace({ pathname: OPENINGS_ROUTE });
      }, 2000);
    }
  }

  onProfileFinishEdit = () => {
    const { opening } = this.state;
    window.location = `/employer/funnels/${idify(opening)}/edit`;
  };

  onProfileFinishPublish = async () => {
    const { push, setErrorFlashMessage } = this.props;
    const { opening } = this.state;

    this.setState({ publishing: true });

    try {
      await apolloClient.mutate({
        mutation: PublishOpeningDocument,
        variables: { openingId: idify(opening) },
      });

      push(createLink({ link: FUNNEL_ROUTE, params: { openingId: idify(opening) } }));
    } catch (_) {
      this.setState({ publishing: false });
      setErrorFlashMessage();
    }
  };

  openProfileFinishModal = () => {
    const { setModalContent, location } = this.props;

    setProfileFinishModal(setModalContent, location.pathName);

    setModalContent({
      className: 'finish-recruiter-modal-wrapper',
      config: { disableClose: true },
      content: <FinishProfileModal />,
      doNotCloseOnLocationChange: true,
    });

    trackEvent({
      context: MetricContext.FINISH_RECRUITER_PROFILE,
      interaction: MetricAction.OPEN_FINISH_RECRUITER_PROFILE_VIEW,
      payload: {
        'Event Source': MetricEventSource.USER_LOGIN,
        'Previous Context': getActiveMetricsContext(location.pathname),
      },
    });
  };

  publish = async () => {
    const { recruiter } = this.props;

    if (recruiter.isDraft) {
      this.openProfileFinishModal(PROFILE_FINISH_FLOWS.PUBLISH);
    } else {
      await this.onProfileFinishPublish();
    }
  };

  onEdit = evt => {
    const { recruiter } = this.props;

    if (recruiter.isDraft) {
      evt.preventDefault();
      this.openProfileFinishModal(PROFILE_FINISH_FLOWS.EDIT);
    }
  };

  saveOpeningPaidAddons(paidAddons) {
    apolloClient.mutate({
      mutation: SaveOpeningDocument,
      variables: {
        openingId: this.state.opening.id,
        openingInput: {
          openingPaidAddons: paidAddons.map(addon => ({
            isRecurring: addon.isRecurring,
            priceInEur: addon.discountedPriceInEur ?? addon.priceInEur ?? 0,
            type: addon.type,
          })),
        },
      },
    });
  }

  changeField = (path, value) => {
    const updatePath = path === 'openingPaidAddons' ? 'opening.paidAddons' : `opening.${path}`;

    this.setState(updeep.updateIn(updatePath, value), () => {
      if (path === 'openingPaidAddons') {
        this.saveOpeningPaidAddons(value);
      } else {
        apolloClient.mutate({
          mutation: SaveOpeningDocument,
          variables: {
            openingId: this.state.opening.id,
            openingInput: { [path]: value },
          },
        });
      }
    });
  };

  renderHeader = () => {
    const { opening } = this.state;

    return (
      <div className="funnel-preview-header">
        <div>
          <span className="heavy">Almost there!</span>
          <br />
          Your position is <span className="purple">ready for publishing.</span>
        </div>

        <a className="funnel-preview-header-edit" href={`/employer/funnels/${opening.id}/edit`} onClick={this.onEdit}>
          <Svg src={EditIconSvg} />
          Edit position details
        </a>
      </div>
    );
  };

  render() {
    const { opening, companyProfile, publishing, relocationBenefits } = this.state;
    const { recruiter, customerStatus, checkSubscriptionState, checkOpeningLimit } = this.props;

    if (!opening) {
      return null;
    }

    return (
      <Fragment>
        <Helmet>
          <title>Opening preview</title>
        </Helmet>
        <FunnelReviewView
          benefits={relocationBenefits}
          changeField={this.changeField}
          checkOpeningLimit={checkOpeningLimit}
          checkSubscriptionState={checkSubscriptionState}
          companyProfile={companyProfile}
          customerStatus={customerStatus}
          header={this.renderHeader}
          opening={opening}
          publish={this.publish}
          publishing={publishing}
          recruiter={recruiter}
        />
      </Fragment>
    );
  }
}
